import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../api';

interface BDUser {
  id: number;
  name: string;
  email: string;
  role: string;
  trust_level: number;
}

/**
 * BD-03. Minimal admin control for trust_level — there is no dedicated BD
 * settings page yet (BD-01, out of scope), so this is a self-contained
 * list+toggle rather than a standalone settings screen. Level 1 lets
 * zoho_note / pm_comment auto-execute on approve; email_send never does,
 * at any level (enforced server-side, not reflected here as a toggle).
 */
const TrustLevelPanel: React.FC = () => {
  const [users, setUsers] = useState<BDUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/bd-approvals/users');
      setUsers(res.data?.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setLevel = async (userId: number, trustLevel: number) => {
    setSavingId(userId);
    try {
      await api.patch(`/api/bd-approvals/users/${userId}/trust-level`, { trustLevel });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, trust_level: trustLevel } : u)));
      toast.success('Trust level updated');
    } catch (err) {
      toast.error('Failed to update trust level');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <div className="mb-6 text-sm text-gray-400">Loading users…</div>;

  return (
    <div className="mb-6 border rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b text-sm font-medium text-gray-700">
        Trust levels — level 1 auto-executes Zoho notes and PM comments on approve; email can never auto-send.
      </div>
      <div className="divide-y max-h-64 overflow-y-auto">
        {users.map((u) => (
          <div key={u.id} className="px-4 py-2 flex items-center justify-between text-sm">
            <div>
              <span className="font-medium text-gray-800">{u.name}</span>
              <span className="text-gray-400 ml-2">{u.email}</span>
            </div>
            <select
              value={u.trust_level}
              disabled={savingId === u.id}
              onChange={(e) => setLevel(u.id, Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm disabled:opacity-50"
            >
              <option value={0}>Level 0 — everything needs approval</option>
              <option value={1}>Level 1 — notes/comments auto-execute</option>
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrustLevelPanel;
